import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import {
  DecisionStatusLog,
  FinancialApplication,
  FinancialApplicationFraudResult,
  IdvRecord,
} from "../../models/applications/applicationInterfaces";
import { DecisionIntelligenceV1 } from "./decisionIntelligence-v1";
import {
  FraudCategoryDisplay,
  fraudCardsForPerson,
  getScope,
} from "../../components/FraudCategoryDisplay";
import {
  getRiskScoreColor,
  NEUTRAL_SCORE_COLOR,
} from "../../components/FraudCategoryCard";

import "./decisionIntelligence.scss";
import { useDispatch, useSelector } from "react-redux";
import {
  getApplicationActivity,
  getDecisionStatusLogs,
  getIdvEvaluations,
  selectApplicationDetail,
  getDecisionStatus,
} from "../../models/applications/applicationDetailSlice";
import { startCase } from "lodash";
import ApplicantSelector from "../../components/ui/applicantSelector";
import CollapsibleSection from "../../components/ui/CollapsibleSection";

/**
 * Which risk band a document-verification decision reads as, so the ID Documents
 * card can use the same palette as the scored cards. Absent from the map — pending,
 * or no attempt at all — means neutral, not green.
 */
const DECISION_RISK_BAND: Record<string, string | undefined> = {
  accept: "low",
  reject: "high",
  resubmit: "medium",
  review: "medium",
};

export const PRIMARY_APPLICANT_SCOPES = ["applicant", "application"];
export const CO_APPLICANT_SCOPES = ["co-applicant", "coapplicant"];
import { selectOrg } from "../../models/organization/organizationSlice";
import ExecutedRulesV2 from "./executedRules-v2";
import { message, Select, Skeleton } from "antd";
import dayjs from "dayjs";
import api from "../../utils/api";
import { ConfirmationModal } from "../../components/confirmationModal";
import BoaEmbed from "../../components/boaEmbed";
import { WatchlistScreening } from "../../models/applications/applicationInterfaces";

/** How the OFAC card ranks one person's screens: worst outcome first. */
const SCREEN_SEVERITY: Record<WatchlistScreening["status"], number> = {
  "potential-match": 3,
  failed: 2,
  clear: 1,
};
export interface DecisionIntelligenceProps {
  effectivData: any;
  decisionStatusLogs?: DecisionStatusLog[];
  applicationsData?: FinancialApplication;
  fraudResultsData?: FinancialApplicationFraudResult[];
}

enum RunRulesMode {
  LIVE = "live",
  TEST = "test",
}

// Stable identity for "no IDV records yet". A fresh `[]` here would give
// `idvRecords` a new reference every render while `idvData` is still loading,
// which propagates through the `people` and `visiblePeople` memos and re-runs
// the tab-fallback effect on every render for no reason.
const NO_IDV_RECORDS: IdvRecord[] = [];

export const DecisionIntelligence: FC<DecisionIntelligenceProps> = ({
  effectivData,
  decisionStatusLogs,
  applicationsData,
  fraudResultsData,
}) => {
  const [activeLog, setActiveLog] = useState<number | undefined>(undefined);
  const [activeTab, setActiveTab] = useState("applicant");
  const [activeModal, setActiveModal] = useState<RunRulesMode | undefined>();
  const [isRunningRules, setIsRunningRules] = useState<
    RunRulesMode | undefined
  >();

  const application = useSelector(selectApplicationDetail);
  const permissions = useMemo(
    () => application.application?.permissions,
    [application]
  );
  // Document verification is a card in the FraudGuard+ strip rather than a section
  // of its own: it is sold as part of that module, and this tab already carries
  // "IDV and liveness check failed or skipped" as a Review reason, so the evidence
  // belongs beside the reason. Same gate the old tab used.
  const idvRecords = application.idvData?.records ?? NO_IDV_RECORDS;
  /**
   * Whether this flow is set up for document verification, and whether the
   * application has anything captured. Together they decide if ID Documents is
   * part of this institution's report at all:
   *
   * - configured, no attempts    → show it, in its zero state (ask for one)
   * - attempts, however arrived  → show it, so the evidence is always readable
   * - neither                    → HIDE it. An institution that has not bought
   *   document verification sees exactly the report it saw before this shipped,
   *   which is what makes merging this safe for everyone already live.
   */
  /**
   * The IDV record a person TAB refers to.
   *
   * The convention is subtle enough to be worth naming once: "applicant" and
   * "coapplicant" are roles and match on `person.role` (note the API's hyphenated
   * "co-applicant"), while every other key already IS a financial person uuid. It
   * was written out at three call sites; a new role, or a renamed key, applied to
   * one of them yields a tab whose panel scopes to nobody.
   */
  const idvRecordForKey = useCallback(
    (key: string) => {
      const wantedRole =
        key === "applicant"
          ? "applicant"
          : key === "coapplicant"
          ? "co-applicant"
          : null;
      return wantedRole
        ? idvRecords.find((record) => record.person.role === wantedRole)
        : idvRecords.find((record) => record.person.id === key);
    },
    [idvRecords]
  );

  /**
   * The newest OFAC (sanctions) screen a person TAB refers to. Screens are
   * scoped by role only — relationship persons are never screened. An
   * `application`-scoped screen is a lone evaluation that could not be tied to a
   * person by SSN, so it goes on the applicant's tab, as Device does. The API
   * returns newest first.
   */
  const watchlistScreeningForKey = useCallback(
    (key: string) => {
      const scope = getScope(key);
      if (!scope) return undefined;
      // Most severe first, newest among equals (the API sends newest first and
      // the sort is stable): a later clear or failed screen must never hide an
      // earlier match from the card.
      return (application.watchlistScreenings ?? [])
        .filter(
          (s) =>
            s.scope === scope ||
            (scope === "applicant" && s.scope === "application")
        )
        .sort(
          (a, b) => SCREEN_SEVERITY[b.status] - SCREEN_SEVERITY[a.status]
        )[0];
    },
    [application.watchlistScreenings]
  );

  const idvConfigured = !!application.idvData?.configured;
  /**
   * Whether a verification can be requested for ANYONE on this application:
   * configured AND this reviewer holds the request permission.
   *
   * Computed once per application, because neither half varies by person. It is
   * the deliberate pairing — `configured` alone was not enough. The permission is
   * seeded to no permission set, so an institution can be fully configured while
   * nobody can act, and a tab offered on that basis leads to a card with no
   * action on it.
   */
  const idvRequestable = idvConfigured && !!application.idvData?.canRequest;
  const hasIdvAttempts = idvRecords.some(
    (record) => record.sessions.length > 0
  );
  // Reading IDV results is governed by the FraudGuard+ permission rather than
  // one of its own: same audience, and the evidence lives in this tab beside
  // the "IDV and liveness check failed or skipped" review reason.
  const canViewIdv = permissions?.includes(
    "view-application-fraud-guard-report"
  );
  /**
   * Which person's attempts the ID Documents card and panel report on, resolved
   * from the single selector above. `applicant` / `coapplicant` are roles, so they
   * map onto the matching IDV record; anything else already IS a person uuid.
   */
  const selectedIdvPerson = useMemo(() => {
    if (activeTab !== "applicant" && activeTab !== "coapplicant") {
      return activeTab;
    }
    return idvRecordForKey(activeTab)?.person.id;
  }, [activeTab, idvRecords]);

  const financialApplication = applicationsData;

  /**
   * Everyone this report can speak about, in one list, because there is one row of
   * person tabs above the cards.
   *
   * The applicant and joint applicant come from the application itself so they are
   * present even when nothing has been verified; document verification then adds
   * anyone else it covers — authorized signers, beneficial owners — who the two-tab
   * shape could never name. Keyed by ROLE for the first two so the fraud cards,
   * whose rows are scoped applicant / co-applicant / application, keep working
   * unchanged; keyed by person uuid for the rest, who have no fraud scope at all.
   */
  const people = useMemo(() => {
    const list: { key: string; name: string; role: string }[] = [
      {
        key: "applicant",
        name: `${financialApplication?.applicant?.firstName || ""} ${
          financialApplication?.applicant?.lastName || ""
        }`.trim(),
        role: "Applicant",
      },
    ];

    if (financialApplication?.coApplicant) {
      list.push({
        key: "coapplicant",
        name: `${financialApplication.coApplicant.firstName || ""} ${
          financialApplication.coApplicant.lastName || ""
        }`.trim(),
        role: "Joint Applicant",
      });
    }

    for (const record of idvRecords) {
      if (
        record.person.role === "applicant" ||
        record.person.role === "co-applicant"
      ) {
        continue;
      }
      list.push({
        key: record.person.id,
        name: `${record.person.firstName ?? ""} ${
          record.person.lastName ?? ""
        }`.trim(),
        role: startCase(record.person.role),
      });
    }

    return list;
  }, [financialApplication, idvRecords]);

  /**
   * Whether ID Documents is part of this institution's report at all — the same
   * test the card itself uses, hoisted because the person tabs need it too.
   * Relationship persons (signers, owners) have no fraud results of their own, so
   * this is the only thing that can put them on a tab.
   */
  const idDocumentsAvailable =
    !!idvRecords.length &&
    !!canViewIdv &&
    !!application.application?.id &&
    (idvRequestable || hasIdvAttempts);

  /**
   * The people who get a tab: those with something to READ, plus everyone if
   * there is something to DO.
   *
   *   has fraud or IDV data of their own        (per person)
   *   OR a verification can be requested here   (once per application)
   *
   * The governing rule is that a tab must never open onto nothing. The per-person
   * half covers anyone with evidence to show. The per-application half covers the
   * reason a person with no data still belongs: a processor can ask them for their
   * ID, and the tab is the only route to that action — but only when the action
   * genuinely exists, which is `idvRequestable`, not `configured`. Offered on
   * `configured` alone, a reviewer without the request permission reached a tab
   * whose only content was a card with no action on it.
   *
   * What that leaves out is deliberate: with nothing requestable and nothing
   * captured, the relationship persons (signers, beneficial owners) that document
   * verification introduced drop back out, and only the applicant and joint
   * applicant remain — the report an institution without this feature saw before.
   *
   * NOT a claim that the report is byte-identical to before: application-scoped
   * cards (Device) now show on the applicant's tab only, since they describe the
   * application's device rather than each person's. That was asked for.
   */
  const visiblePeople = useMemo(
    () =>
      people.filter((person) => {
        // Per person: fraud results of their own.
        if (fraudCardsForPerson(fraudResultsData, person.key).length > 0) {
          return true;
        }

        // Per person: an OFAC screen of their own. Same permission as the rest
        // of the report.
        if (canViewIdv && watchlistScreeningForKey(person.key)) return true;

        // Everything below is ID Documents, which is not on this report at all.
        if (!idDocumentsAvailable) return false;

        // Once per application: anyone can be asked, so everyone gets a tab.
        if (idvRequestable) return true;

        // Per person: nothing can be requested, so only evidence earns a tab.
        return !!idvRecordForKey(person.key)?.sessions.length;
      }),
    [
      people,
      fraudResultsData,
      canViewIdv,
      watchlistScreeningForKey,
      idDocumentsAvailable,
      idvRequestable,
      idvRecordForKey,
    ]
  );

  // A tab can stop being shown while it is selected — a person's cards are data,
  // and data arrives late. Falling back keeps the strip from rendering empty
  // under a tab that is no longer there.
  useEffect(() => {
    if (
      visiblePeople.length &&
      !visiblePeople.some((person) => person.key === activeTab)
    ) {
      setActiveTab(visiblePeople[0].key);
    }
  }, [visiblePeople, activeTab]);

  const org = useSelector(selectOrg);
  const dispatch = useDispatch();

  // Filter decision status logs to only include logs with availableDecisionRules
  // Sort by createdAt in descending order (most recent first)
  const filteredDecisionStatusLogs = useMemo(() => {
    if (!decisionStatusLogs) {
      return [];
    }
    return decisionStatusLogs
      .filter((log) =>
        Object.prototype.hasOwnProperty.call(log, "availableDecisionRules")
      )
      .sort((a, b) => dayjs(b.createdAt).diff(dayjs(a.createdAt)));
  }, [decisionStatusLogs, application]);

  const runRules = async (testMode: boolean) => {
    if (isRunningRules) return; // Prevent multiple concurrent calls
    setActiveModal(undefined);
    const id = application?.application?.id;

    try {
      setIsRunningRules(testMode ? RunRulesMode.TEST : RunRulesMode.LIVE);
      await api.post(`/submissions/${id}/decision-rules/run`, {
        testMode,
      });
      await dispatch(getApplicationActivity(id as number));
      await dispatch(getDecisionStatusLogs(id as number));
      await dispatch(getDecisionStatus(id as number));
      message.success(
        isRunningRules === RunRulesMode.LIVE
          ? "Decison Rules Evaluation Complete"
          : "Decision Rules Test Complete"
      );
    } catch (e) {
      message.error("Failed to run rules. Please try again.");
    } finally {
      setIsRunningRules(undefined);
    }
  };

  const idDocumentsCard = useMemo(() => {
    // `idDocumentsAvailable` is the gate — the same one the person tabs use,
    // rather than a second copy of it, since two negations of one rule a hundred
    // lines apart is how the tabs and the card come to disagree about whether the
    // feature exists. The id check is repeated only to narrow it for the path
    // below; the boolean cannot do that.
    if (!idDocumentsAvailable || !application.application?.id) {
      return undefined;
    }

    // The attempts belonging to the person selected above — not the whole
    // application. With one row of tabs driving everything, a card that averaged
    // across people would contradict the tab the reviewer is looking at.
    const attempts = (idvRecordForKey(activeTab)?.sessions ?? [])
      .slice()
      .sort((a, b) =>
        (b.completedAt ?? b.createdAt ?? "").localeCompare(
          a.completedAt ?? a.createdAt ?? ""
        )
      );

    /**
     * The one attempt this card reports on: the most recent that produced a score,
     * else the most recent that reached a decision, else the newest. Score and
     * colour must come from the SAME attempt, or the card contradicts itself while
     * a re-request is in flight.
     */
    const reported =
      attempts.find(
        (attempt) => attempt.score !== null && attempt.score !== undefined
      ) ??
      attempts.find((attempt) => !!attempt.decision) ??
      attempts[0];

    /**
     * A newer verification is outstanding while `reported` still describes an
     * earlier, completed one.
     *
     * `reported` deliberately prefers the last attempt that carries a score, so
     * re-requesting someone who already passed leaves this card showing their old
     * score — and `DECISION_RISK_BAND` takes it GREEN on that old accept. Two
     * surfaces then both assert verified (the panel's badge does the same) while a
     * capture link sits in the applicant's inbox. The note says so on the card
     * without moving the number off the attempt that actually produced evidence.
     */
    // Any attempt that is still in play and not yet superseded means a capture link
    // is outstanding — NOT "the newest attempt happens to be pending".
    //
    // The list is sorted by (completedAt ?? createdAt), and a superseded attempt
    // can still finish: the API explicitly supports an applicant completing an old
    // capture link after a re-request. That attempt then sorts FIRST on its later
    // completedAt, so `newest` was not the pending one, the note was suppressed,
    // and the card painted the old accept green while the re-requested link was
    // still outstanding — exactly the state the note exists to report.
    const pendingRequest = attempts.some(
      (attempt) =>
        attempt.isPending &&
        !attempt.supersededAt &&
        attempt.id !== reported?.id
    );

    /**
     * Nobody has asked this person for their ID yet.
     *
     * Distinguished from "asked, and there is nothing to report" because the card
     * renders both as a neutral dash, and the two mean opposite things to a
     * processor: one is an action they can take, the other is data they are
     * waiting on. On-demand verification is the case that suffers — the processor
     * is the one initiating, so they are the one who needs the prompt, and an
     * empty value reads as missing data rather than as an invitation.
     */
    const neverRequested = attempts.length === 0;

    /**
     * Asked, and still waiting on the applicant.
     *
     * The third case the dash used to swallow. It only shows where there is no
     * number to show — a completed attempt whose score arrived is described by
     * that score, not by the state of a later one, which is what
     * `pendingRequest` above is for.
     */
    const awaitingCapture = !reported?.score && !!reported?.isPending;

    return {
      score: reported?.score ?? undefined,
      // Only the states that have NO number to report get a caption; a caption
      // beside a number widened this card past its neighbours and clipped.
      note: neverRequested
        ? "Not requested"
        : awaitingCapture
        ? "Pending"
        : undefined,
      // The one thing that has to coexist with a number, so it is an icon and a
      // tooltip — and being a tooltip, it can afford to say the whole thing.
      // Worded for both cases: the completed attempt it reports may itself have
      // no score, in which case the card shows a dash and "this score" would be
      // describing something that is not on screen.
      noteTooltip: pendingRequest
        ? "A newer verification has been requested and is still outstanding. What this card reports is from the verification already completed."
        : undefined,
      // Document verification has no risk score to band, so the DECISION picks the
      // band — from the strip's own palette rather than a second set of hexes here.
      // Neutral when there is no decision at all: a verification still in flight is
      // not a pass, and the card already greys a value it does not have.
      color: DECISION_RISK_BAND[reported?.decision ?? ""]
        ? getRiskScoreColor(DECISION_RISK_BAND[reported?.decision ?? ""])
        : NEUTRAL_SCORE_COLOR,
      detail: selectedIdvPerson ? (
        // No person tabs here: the row above the cards already picked the person,
        // and a second identical row read as a rendering fault.
        <BoaEmbed
          title="Document Verification"
          initialHeight={420}
          path={`/organization/${org?.slug}/applications/${application.application.id}/idv?personId=${selectedIdvPerson}`}
          // A request made inside the panel changes what THIS card reports, and
          // the card reads from a store filled once on page load. Refetch on the
          // panel's say-so, or the card keeps showing the previous accept — in
          // green, with no pending note — while a new capture link is out.
          onDataChanged={() => {
            dispatch(getIdvEvaluations(String(application.application?.id)));
          }}
        />
      ) : (
        // No IDV record for the selected person, so there is no panel to scope to
        // them. Interpolating the missing id sent `personId=undefined`, which the
        // API narrowed to a uuid matching nobody — the panel then rendered its
        // "nothing requested yet" state under a card that was still showing a
        // value, which reads as a contradiction rather than an absence.
        <p className="mb-0" style={{ color: "#8c8c8c" }}>
          No document verification on record for this person.
        </p>
      ),
    };
  }, [
    idvRecords,
    selectedIdvPerson,
    activeTab,
    idvRecordForKey,
    canViewIdv,
    idDocumentsAvailable,
    application.application?.id,
    dispatch,
    org?.slug,
  ]);

  /**
   * The OFAC card: the sanctions screen run inside the fraud evaluation, for the
   * person selected above. Absent when the person has no screen on record and
   * none was attempted, so institutions that do not screen here see the strip
   * they saw before. "Failed" is different: a screen was attempted and
   * rejected, so this person has no OFAC evidence — a finding, not an empty
   * state.
   */
  const watchlistCard = useMemo(() => {
    const scope = getScope(activeTab);
    if (!canViewIdv || !scope || !application.application?.id) {
      return undefined;
    }
    const screening = watchlistScreeningForKey(activeTab);
    const unavailable = !!application.watchlistScreeningsUnavailable;
    if (!screening && !unavailable) {
      return undefined;
    }

    const face = !screening
      ? // The load failed: say so rather than drop the card, which would read
        // as "no hit". The panel below makes its own request.
        {
          color: NEUTRAL_SCORE_COLOR,
          note: "Unavailable",
          noteTooltip:
            "The OFAC screen could not be loaded. Open the card to try again.",
        }
      : screening.status === "clear"
      ? { value: "Clear", color: getRiskScoreColor("low") }
      : screening.status === "potential-match"
      ? // The card is labelled OFAC, so its face must not claim an OFAC hit
        // for an enforcement list (most hits are OIG exclusions).
        screening.ofacMatch
        ? {
            value: "Match",
            color: getRiskScoreColor("high"),
            noteTooltip:
              "Matched an OFAC sanctions list. Open for the matched entity.",
          }
        : {
            value: "List match",
            color: getRiskScoreColor("medium"),
            noteTooltip:
              "Matched a sanctions or enforcement list other than OFAC's. Open for details.",
          }
      : screening.status === "failed"
      ? {
          value: "Failed",
          color: getRiskScoreColor("medium"),
          noteTooltip:
            "The sanctions screen did not complete for this person, so there is no OFAC screen on record. Open for the reason.",
        }
      : // A status this build does not know (a newer API): say nothing
        // rather than guess.
        { value: "—", color: NEUTRAL_SCORE_COLOR };

    return {
      ...face,
      detail: (
        <BoaEmbed
          title="OFAC"
          initialHeight={240}
          path={`/organization/${org?.slug}/applications/${application.application.id}/watchlist?scope=${scope}`}
        />
      ),
    };
  }, [
    watchlistScreeningForKey,
    activeTab,
    canViewIdv,
    application.application?.id,
    application.watchlistScreeningsUnavailable,
    org?.slug,
  ]);

  // Generate options for the decision log selector
  const logSelectOptions = useMemo(() => {
    if (!filteredDecisionStatusLogs) {
      return [];
    }
    return filteredDecisionStatusLogs?.map((log, index) => {
      const timestamp = dayjs(log.createdAt);
      const formattedTimestamp = timestamp.format("MMM D, YYYY @h:mm:ssA");
      return {
        label: `Check: ${formattedTimestamp}`,
        value: index,
      };
    });
  }, [filteredDecisionStatusLogs]);

  useEffect(() => {
    if (isRunningRules) return;
    if (logSelectOptions && logSelectOptions.length > 0) {
      setActiveLog(0); // Most recent log is now at index 0
    }
  }, [logSelectOptions, isRunningRules]);

  if (!permissions?.includes("view-application-decision-intelligence-report")) {
    return <p>You don't have permission to view this page.</p>;
  }

  // Get query parameter
  const urlParams = new URLSearchParams(window.location.search);
  const decisionIntelligenceParam = urlParams.get("decisionintelligence");

  // Load the version of Decision Intelligence based on the org's meta data
  // * Organizations will default to V1 unless they have explicitly set the meta data to v2 or
  // * queryParam decisionintelligence=v2 is set
  if (
    org?.meta?.enableDecisionIntelligence &&
    org.meta.enableDecisionIntelligence !== "v2" &&
    decisionIntelligenceParam !== "v2"
  ) {
    return (
      <DecisionIntelligenceV1
        effectivData={effectivData}
        decisionStatusLogs={decisionStatusLogs}
        applicationsData={applicationsData}
        fraudResultsData={fraudResultsData}
      />
    );
  }

  // Otherwise, display the new version (V2)
  return (
    <>
      <div className="d-flex justify-content-between align-items-center">
        <Select
          value={activeLog}
          className="mb-2"
          defaultValue={0} // Default to the most recent check
          size="large"
          style={{
            width: "350px",
            visibility:
              filteredDecisionStatusLogs?.length > 0 ? "visible" : "hidden",
          }}
          notFoundContent="No reports available"
          options={logSelectOptions}
          onChange={(logIndex) =>
            logIndex !== undefined && setActiveLog(logIndex)
          }
        />
        {/* Rerun Rules Button - Only shown if user has permission to rerun rules */}
        {permissions?.includes("run-application-decision-rules") && (
          <div className="d-flex align-items-center">
            <button
              disabled={!!isRunningRules}
              style={{
                height: "42px",
                width: "100px",
              }}
              className="m-2 br-1 btn btn-outline-primary"
              onClick={() => {
                setActiveModal(RunRulesMode.TEST);
              }}
            >
              {isRunningRules === RunRulesMode.TEST ? `Running...` : `Run Test`}
            </button>
            <button
              disabled={!!isRunningRules}
              style={{ height: "42px", width: "100px" }}
              className="btn btn-primary"
              onClick={() => {
                setActiveModal(RunRulesMode.LIVE);
              }}
            >
              {isRunningRules === RunRulesMode.LIVE ? `Running...` : `Run Live`}
            </button>
          </div>
        )}
      </div>
      {/* Decision Intelligence Running Animation */}
      {isRunningRules ? (
        <>
          <Skeleton.Input
            className="mb-2 w-100"
            active={true}
            style={{ height: "150px" }}
          />
          <Skeleton.Input
            className="w-100 mb-2"
            active={true}
            style={{ height: "200px" }}
          />
          <Skeleton.Input
            className="w-100 mb-2"
            active={true}
            style={{ height: "125px" }}
          />{" "}
        </>
      ) : (
        // Executed Rules from Returned Decision Log
        <ExecutedRulesV2
          decisionStatusLog={
            filteredDecisionStatusLogs && activeLog !== undefined
              ? filteredDecisionStatusLogs[activeLog]
              : undefined
          }
          className="mt-2 mb-4"
        />
      )}
      {/* No person has a card to show, so there is no report to draw. This is the
          gate that keeps an institution without document verification on exactly
          the report they had before — and it supersedes the raw data checks: data
          nobody's tab can reach is not a report. */}
      {!!visiblePeople.length && (
        <CollapsibleSection
          title="FraudGuard+ Report"
          className="mt-3"
          // A report with something to say should not need a click to say it. Safe
          // as an uncontrolled default because the section only mounts once there
          // is data — see the gate above.
          defaultExpanded
        >
          <ApplicantSelector
            activeTab={activeTab}
            onTabClick={setActiveTab}
            people={visiblePeople}
          />
          <FraudCategoryDisplay
            fraudResultsData={fraudResultsData}
            applicantType={activeTab}
            idDocuments={idDocumentsCard}
            watchlist={watchlistCard}
          />
        </CollapsibleSection>
      )}
      <ConfirmationModal
        show={activeModal === RunRulesMode.TEST}
        title="Confirm Rules Test"
        onClose={() => setActiveModal(undefined)}
        cta={
          <button
            onClick={() => runRules(true)}
            className="btn btn-primary mr-2"
          >
            Confirm
          </button>
        }
      >
        <p>
          Running rules in test mode will not affect the application's status.
        </p>
      </ConfirmationModal>
      <ConfirmationModal
        show={activeModal === RunRulesMode.LIVE}
        title="Confirm Live Rules Evaluation"
        onClose={() => setActiveModal(undefined)}
        cta={
          <div className="d-flex">
            <button
              onClick={() => runRules(false)}
              className="btn btn-primary mr-2"
            >
              Confirm
            </button>
          </div>
        }
      >
        <p>
          Rerunning rules can update the current status of the application. In
          addition:
        </p>
        <ul>
          <li>The application may be booked to the Core database.</li>
          <li>
            Automated notification emails associated with the new status will be
            sent.
          </li>
          <li>Additional exports of the application data may be triggered.</li>
        </ul>
      </ConfirmationModal>
    </>
  );
};
